import { makeAutoObservable, observable, runInAction } from "mobx";
import Decimal from "decimal.js";
import {
  CreateItem,
  GetQuery,
  PendingTransaction,
  SaveResult,
  SelectedSouvenir,
  ServiceResult,
  TransactionType,
  BillSouvenir,
} from "../models/Souvenir";
import {
  GetSouvenirs,
  CreateSouvenirTransaction,
} from "../services/Souvenir";

const SouvenirChangedMessage =
  "รายการของแถมมีการเปลี่ยนแปลง กรุณาตรวจสอบอีกครั้งก่อนทำรายการ";

const getSnapshotKey = (
  funding: number,
  items: SelectedSouvenir[]
): string => {
  const sortedItems = [...items].sort((first, second) => {
    if (first.souvenirCode < second.souvenirCode) return -1;
    if (first.souvenirCode > second.souvenirCode) return 1;
    return 0;
  });

  return JSON.stringify({
    funding,
    souvenir: sortedItems.map((item) => ({
      souvenirCode: item.souvenirCode,
      souvenirName: item.souvenirName,
      cost: item.cost,
      quantity: item.quantity,
      unit: item.unit,
    })),
  });
};

export default class SouvenirStore {
  query: GetQuery | null = null;
  items: SelectedSouvenir[] = [];
  funding = 0;
  expanded = false;
  isLoading = false;
  isSubmitting = false;
  errorMessage = "";
  noticeMessage = "";
  needsRevalidation = false;
  pending = new Map<TransactionType, PendingTransaction>();

  private queryKey = "";
  private requestVersion = 0;
  private snapshotKey: string | null = null;
  private savedTransactions = new Set<string>();

  constructor() {
    makeAutoObservable(
      this,
      {
        pending: observable.shallow,
      },
      {
        autoBind: true,
      }
    );
  }

  get billSouvenirs(): BillSouvenir[] {
    return this.items
      .filter((item) => item.selectedQuantity > 0)
      .map((item) => ({
        souvenirCode: item.souvenirCode,
        souvenirName: item.souvenirName,
        quantity: item.selectedQuantity,
        unit: item.unit,
      }));
  }

  get usedFundingDecimal() {
    return this.items.reduce(
      (total, item) =>
        total.plus(new Decimal(item.cost).times(item.selectedQuantity)),
      new Decimal(0)
    );
  }

  get usedFunding() {
    return this.usedFundingDecimal.toNumber();
  }

  get remainingFunding() {
    return new Decimal(this.funding)
      .minus(this.usedFundingDecimal)
      .toNumber();
  }

  get currentPending() {
    return this.query
      ? this.pending.get(this.query.transactionType)
      : undefined;
  }

  get isReadOnly() {
    return (
      this.isSubmitting ||
      this.needsRevalidation ||
      Boolean(this.currentPending)
    );
  }

  clearSelection() {
    this.requestVersion += 1;
    this.items = [];
    this.funding = 0;
    this.expanded = false;
    this.isLoading = false;
    this.errorMessage = "";
    this.noticeMessage = "";
    this.needsRevalidation = false;
    this.snapshotKey = null;
  }

  onClearView() {
    this.clearSelection();
    this.query = null;
    this.queryKey = "";
  }

  onReset(transactionType: TransactionType, clearPending: boolean = true) {
    if (this.isSubmitting) return;

    if (clearPending) {
      this.pending.delete(transactionType);
    }

    this.clearSelection();
    this.queryKey = "";
  }

  onSetQuery(query: GetQuery, contextKey: string) {
    const key = JSON.stringify([
      query.transactionType,
      query.weight ?? null,
      query.price ?? null,
      query.labour ?? null,
      query.interest ?? null,
      contextKey,
    ]);

    if (this.queryKey === key) return;

    const shouldShowNotice =
      this.query?.transactionType === query.transactionType &&
      (
        this.snapshotKey !== null ||
        this.noticeMessage === SouvenirChangedMessage
      );

    this.clearSelection();
    this.query = { ...query };
    this.queryKey = key;

    if (shouldShowNotice) {
      this.noticeMessage = SouvenirChangedMessage;
    }
  }

  async onExpandedChange(expanded: boolean) {
    if (this.isSubmitting || !this.query) return;

    this.expanded = expanded;
    this.requestVersion += 1;

    const version = this.requestVersion;

    if (!expanded) {
      this.isLoading = false;
      return;
    }

    const previousSnapshotKey = this.snapshotKey;

    const previousSelections = new Map(
      this.items
        .filter((item) => item.selectedQuantity > 0)
        .map(
          (item) =>
            [
              item.souvenirCode,
              item.selectedQuantity,
            ] as [string, number]
        )
    );

    this.errorMessage = "";
    this.isLoading = true;
    this.needsRevalidation = true;

    const query = { ...this.query };

    try {
      // Refresh options when opening the section or revalidating before saving.
      const response = await GetSouvenirs(query);

      if (version !== this.requestVersion) return;

      if (!response.success) {
        runInAction(() => {
          this.errorMessage = response.message;
        });
        return;
      }

      if (!response.data || !Array.isArray(response.data.souvenir)) {
        throw new Error("ข้อมูลของแถมไม่ถูกต้อง กรุณาโหลดข้อมูลใหม่");
      }

      const funding = Number(response.data.funding);

      const items: SelectedSouvenir[] = response.data.souvenir.map(
        (item) => ({
          ...item,
          cost: Number(item.cost),
          quantity: Number(item.quantity),
          selectedQuantity:
            previousSelections.get(item.souvenirCode) ?? 0,
        })
      );

      const codes = new Set(
        items.map((item) => item.souvenirCode)
      );

      const isValid =
        Number.isFinite(funding) &&
        funding >= 0 &&
        codes.size === items.length &&
        items.every(
          (item) =>
            typeof item.souvenirCode === "string" &&
            item.souvenirCode.trim().length > 0 &&
            Number.isFinite(item.cost) &&
            item.cost >= 0 &&
            Number.isSafeInteger(item.quantity) &&
            item.quantity >= 0
        );

      if (!isValid) {
        throw new Error("ข้อมูลของแถมไม่ถูกต้อง กรุณาโหลดข้อมูลใหม่");
      }

      const nextSnapshotKey = getSnapshotKey(funding, items);

      const hasResponseChanged =
        previousSnapshotKey !== null &&
        previousSnapshotKey !== nextSnapshotKey;

      const selectedFunding = items.reduce(
        (total, item) =>
          total.plus(
            new Decimal(item.cost).times(item.selectedQuantity)
          ),
        new Decimal(0)
      );

      const canRestoreSelection =
        Array.from(previousSelections.keys()).every((code) =>
          codes.has(code)
        ) &&
        items.every(
          (item) =>
            Number.isSafeInteger(item.selectedQuantity) &&
            item.selectedQuantity >= 0 &&
            item.selectedQuantity <= item.quantity
        ) &&
        selectedFunding.lte(funding);

      const shouldClearSelection =
        hasResponseChanged || !canRestoreSelection;

      runInAction(() => {
        this.funding = funding;

        this.items = shouldClearSelection
          ? items.map((item) => ({
              ...item,
              selectedQuantity: 0,
            }))
          : items;

        this.snapshotKey = nextSnapshotKey;
        this.needsRevalidation = false;

        if (shouldClearSelection) {
          this.noticeMessage = SouvenirChangedMessage;
        }
      });
    } catch (error: unknown) {
      if (version !== this.requestVersion) return;

      runInAction(() => {
        this.errorMessage =
          error instanceof Error
            ? error.message
            : "โหลดข้อมูลของแถมไม่สำเร็จ";
      });
    } finally {
      if (version === this.requestVersion) {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    }
  }

  canIncrease(souvenirCode: string) {
    if (this.isReadOnly || this.isLoading) return false;

    const item = this.items.find(
      (value) => value.souvenirCode === souvenirCode
    );

    if (!item || item.selectedQuantity >= item.quantity) {
      return false;
    }

    return this.usedFundingDecimal
      .plus(item.cost)
      .lte(this.funding);
  }

  canDecrease(souvenirCode: string) {
    if (this.isReadOnly || this.isLoading) return false;

    return Boolean(
      this.items.find(
        (item) =>
          item.souvenirCode === souvenirCode &&
          item.selectedQuantity > 0
      )
    );
  }

  onQuantityChange(souvenirCode: string, quantity: number) {
    if (
      this.isReadOnly ||
      this.isLoading ||
      !Number.isSafeInteger(quantity) ||
      quantity < 0
    ) {
      return;
    }

    const item = this.items.find(
      (value) => value.souvenirCode === souvenirCode
    );

    if (
      !item ||
      quantity > item.quantity ||
      quantity === item.selectedQuantity
    ) {
      return;
    }

    const nextFunding = this.usedFundingDecimal
      .minus(new Decimal(item.cost).times(item.selectedQuantity))
      .plus(new Decimal(item.cost).times(quantity));

    if (nextFunding.gt(this.funding)) return;

    item.selectedQuantity = quantity;
    this.noticeMessage = "";
  }

  async onCheckBeforePreview(
    transactionType: TransactionType
  ): Promise<"continue" | "warning" | "stale"> {
    if (!this.query || this.query.transactionType !== transactionType) {
      return "continue";
    }

    const query = { ...this.query };
    const queryKey = this.queryKey;
    const version = this.requestVersion;

    try {
      // Before preview: warn when options exist but none are selected.
      // Do not check stock or funding, or replace the reviewed snapshot.
      const response = await GetSouvenirs(query);

      if (
        queryKey !== this.queryKey ||
        version !== this.requestVersion
      ) {
        return "stale";
      }

      if (
        response.success &&
        Array.isArray(response.data?.souvenir) &&
        response.data.souvenir.length > 0 &&
        !this.items.some((item) => item.selectedQuantity > 0)
      ) {
        return "warning";
      }

      // A failed preview check does not bypass the final submit validation.
      return "continue";
    } catch {
      if (
        queryKey !== this.queryKey ||
        version !== this.requestVersion
      ) {
        return "stale";
      }

      return "continue";
    }
  }

  async onPrepareForSubmit(
    transactionType: TransactionType
  ): Promise<ServiceResult<CreateItem[]>> {
    const preparation = this.onPrepare(transactionType);

    if (!preparation.success) {
      return preparation;
    }

    const previousSnapshotKey = this.snapshotKey;
    const wasExpanded = this.expanded;

    const hasUnreviewedQueryChange =
      previousSnapshotKey === null &&
      this.noticeMessage === SouvenirChangedMessage;

    // onExpandedChange increments the version before fetching.
    const expectedVersion = this.requestVersion + 1;

    // Before saving and printing: revalidate and stop on errors or changed data.
    await this.onExpandedChange(true);

    // Ignore results invalidated by a query change, reset, or newer request.
    if (this.requestVersion !== expectedVersion) {
      return {
        success: false,
        message: SouvenirChangedMessage,
      };
    }

    if (
      this.errorMessage ||
      this.needsRevalidation ||
      this.snapshotKey === null
    ) {
      return {
        success: false,
        message:
          this.errorMessage ||
          "ตรวจสอบข้อมูลของแถมไม่สำเร็จ กรุณาโหลดข้อมูลใหม่ก่อนทำรายการ",
      };
    }

    const hasResponseChanged =
      previousSnapshotKey !== null &&
      previousSnapshotKey !== this.snapshotKey;

    if (hasResponseChanged || hasUnreviewedQueryChange) {
      runInAction(() => {
        this.items = this.items.map((item) => ({
          ...item,
          selectedQuantity: 0,
        }));
        this.noticeMessage = SouvenirChangedMessage;
        this.expanded = true;
      });

      return {
        success: false,
        message: SouvenirChangedMessage,
      };
    }

    runInAction(() => {
      this.expanded = wasExpanded;
    });

    return this.onPrepare(transactionType);
  }

  onPrepare(
    transactionType: TransactionType
  ): ServiceResult<CreateItem[]> {
    const pending = this.pending.get(transactionType);

    if (pending) {
      return {
        success: false,
        message: pending.message,
      };
    }

    if (this.isLoading || this.isSubmitting) {
      return {
        success: false,
        message: "กรุณารอการทำงานของส่วนของแถมให้เสร็จก่อน",
      };
    }

    if (!this.query || this.query.transactionType !== transactionType) {
      return {
        success: false,
        message: "ข้อมูลของแถมยังไม่พร้อม กรุณาลองทำรายการอีกครั้ง",
      };
    }

    if (this.needsRevalidation) {
      return {
        success: false,
        message:
          "กรุณาเปิดส่วนของแถมและโหลดข้อมูลให้สำเร็จก่อนทำรายการ",
      };
    }

    const isValid =
      this.usedFundingDecimal.lte(this.funding) &&
      this.items.every(
        (item) =>
          Number.isSafeInteger(item.selectedQuantity) &&
          item.selectedQuantity >= 0 &&
          item.selectedQuantity <= item.quantity
      );

    if (!isValid) {
      return {
        success: false,
        message: "กรุณาตรวจสอบจำนวนของแถมอีกครั้ง",
      };
    }

    return {
      success: true,
      data: this.items
        .filter((item) => item.selectedQuantity > 0)
        .map((item) => ({
          souvenirCode: item.souvenirCode,
          quantity: item.selectedQuantity,
        })),
    };
  }

  async onSave(
    transactionType: TransactionType,
    transactionId: string | null | undefined,
    souvenirs: CreateItem[],
    selectionType: TransactionType = transactionType
  ): Promise<SaveResult> {
    const pending = this.pending.get(selectionType);

    if (pending) {
      return {
        success: false,
        message: pending.message,
      };
    }

    if (this.isSubmitting) {
      return {
        success: false,
        message: "กำลังบันทึกของแถม กรุณารอสักครู่",
      };
    }

    if (
      typeof transactionId !== "string" ||
      transactionId.trim() === ""
    ) {
      const message =
        "สร้างรายการหลักแล้ว แต่ไม่พบรหัสบิลสำหรับบันทึกของแถม " +
        "กรุณาตรวจสอบรายการเดิมก่อนทำรายการใหม่";

      this.pending.set(selectionType, {
        transactionType,
        transactionId: "",
        message,
      });

      return {
        success: false,
        message,
      };
    }

    const key = `${transactionType}:${transactionId}`;

    if (this.savedTransactions.has(key)) {
      return { success: true };
    }

    if (!souvenirs.length) {
      this.savedTransactions.add(key);
      return { success: true };
    }

    const message =
      `รายการ ${transactionId} สร้างแล้ว แต่ยังยืนยันผลการบันทึกของแถมไม่ได้ ` +
      "ระบบหยุดสร้างบิล กรุณาตรวจสอบรายการเดิมก่อนทำรายการใหม่";

    this.isSubmitting = true;
    this.pending.set(selectionType, {
      transactionType,
      transactionId,
      message,
    });

    try {
      const response = await CreateSouvenirTransaction({
        transactionType,
        transactionId,
        souvenirs: souvenirs.map((item) => ({ ...item })),
      });

      if (!response.success) {
        const errorMessage = `${message} (${response.message})`;

        runInAction(() => {
          this.pending.set(selectionType, {
            transactionType,
            transactionId,
            message: errorMessage,
          });
        });

        return {
          success: false,
          message: errorMessage,
        };
      }

      runInAction(() => {
        this.savedTransactions.add(key);
        this.pending.delete(selectionType);
      });

      return { success: true };
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? `${message} (${error.message})`
          : message;

      runInAction(() => {
        this.pending.set(selectionType, {
          transactionType,
          transactionId,
          message: errorMessage,
        });
      });

      return {
        success: false,
        message: errorMessage,
      };
    } finally {
      runInAction(() => {
        this.isSubmitting = false;
      });
    }
  }
}